import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import { fetchAll } from 'config/fetchAll';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Pagination from 'reusable/pagination';
import Export from 'reusable/export';
import { CsvCondition } from 'config/csv';
import DeviceTable from './DeviceTable';
import AddButton from 'reusable/addButton';
import AddDialog from 'reusable/Dialog/addDialog';
import EditDialog from 'reusable/Dialog/editDialog';
import DeleteDialog from 'reusable/Dialog/deleteDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
  sortingOptions,
} from 'reusable/Dialog/dialogInterface';
import {
  addDeviceFields,
  deleteDeviceFields,
  editDeviceFields,
} from './DeviceField';
import { useTranslation } from 'react-i18next';
import ErrorPopup from 'reusable/ErrorPopup';
import { classifiedErrorMessages } from 'config/apiErrors';
import { submitIdempotentCreate } from 'config/idempotentCreate';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { id } from 'date-fns/locale';

const DeviceManagement: React.FC = () => {
  const { t } = useTranslation(['device', 'common']);
  // 保存・削除に失敗したときの説明。以前はコンソールに出すだけだった。
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const showSaveError = (error: unknown) =>
    setErrorMessages(
      classifiedErrorMessages(error, {
        network: t('ui.error-network', { ns: 'common' }),
        forbidden: t('ui.error-forbidden', { ns: 'common' }),
        server: t('ui.error-server', { ns: 'common' }),
        fallback: t('ui.save-failed', { ns: 'common' }),
      })
    );
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [deviceData, setDeviceData] = useState<any[]>([]);
  // EV-142。検索中・失敗時にも、出力する行と実際の取得条件を一致させる。
  const [exportConditions, setExportConditions] = useState<CsvCondition[]>([['page', 1]]);
  const [count, setCount] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>('');
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchProfilePicture = async (userId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${userId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for user ${userId}`,
        error
      );
      return null;
    }
  };

  const fetchDeviceData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      let apiUrl = `/v1/devices/?order_by=${sortValue}&page=${currentPage}&page_size=10&search=${encodeURIComponent(searchValue)}`;

      const response = await axiosInstance.get(apiUrl);
      const fetchedDeviceData = response.data.results;

      const updatedDeviceData = await Promise.all(
        fetchedDeviceData.map(async (device: any) => {
          if (device.user_info?.id) {
            const profilePicture = await fetchProfilePicture(
              device.user_info.id
            );
            return {
              ...device,
              user_info: {
                ...device.user_info,
                profile_picture: profilePicture,
              },
            };
          }
          return device;
        })
      );

      if (!isCurrent()) return;
      setDeviceData(updatedDeviceData);
      setExportConditions([['search', searchValue], ['sort', sortValue], ['page', currentPage]]);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch data:', error);
      setFetchError(error);
    }
  }, [searchValue, sortValue, currentPage, beginRequest]);

  useEffect(() => {
    fetchDeviceData();
  }, [fetchDeviceData, currentDeviceId]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Add) setIsAddDialogOpen(true);
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
    if (dialogType === DialogType.Delete) setIsDeleteDialogOpen(true);
  };

  // EV-322（ADM-15）。割当先の運転者の候補の要求。閉じた後・開き直した後に届いた応答は捨てる。
  const beginDriversRequest = useLatestRequest();

  const closeDialog = () => {
    beginDriversRequest();
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setCurrentDeviceId(null);
  };

  /** 「状態」の選択肢（固定の3つと、割当先の運転者）。 */
  const assignUserOptions = (drivers: any[]) => [
    { id: null, value: 'available', label: t('status.available') },
    { id: null, value: 'in_repair', label: t('status.in_repair') },
    { id: null, value: 'damaged', label: t('status.damaged') },
    ...drivers.map((driver: any) => ({
      id: driver.id,
      value: `using:${driver.id}`,
      label: driver.name,
    })),
  ];

  /**
   * EV-322。運転者の候補を読めなかったとき、「状態」の欄の注記（既存の文言と失敗の種類、再読み込み）で出す。
   * 以前は console だけで、候補が無いのと区別できなかった。
   */
  const withDriversNote = (fields: DialogField[], error: unknown): DialogField[] =>
    fields.map((field) =>
      field.name === 'status'
        ? { ...field, fetchError: error ?? undefined, onRetry: error ? retryDriverOptions : undefined }
        : field
    );

  /** EV-322。開いたままのダイアログの「状態」の候補だけを読み直す（入力は残す）。 */
  const retryDriverOptions = async () => {
    const isCurrent = beginDriversRequest();
    setDialogProps((prev) => ({ ...prev, fields: withDriversNote(prev.fields, null) }));
    try {
      const drivers = await fetchAll<any>('/v1/drivers/');
      if (!isCurrent()) return;
      setDialogProps((prev) => ({
        ...prev,
        fields: prev.fields.map((field) =>
          field.name === 'status' ? { ...field, options: assignUserOptions(drivers) } : field
        ),
      }));
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching drivers data', error);
      setDialogProps((prev) => ({ ...prev, fields: withDriversNote(prev.fields, error) }));
    }
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    const isCurrent = beginDriversRequest();
    let drivers: any[] = [];
    let driversError: unknown = null;

    try {
      // BE-21。page_size=0では全件が返らないため、ページを順に読む。
      drivers = await fetchAll<any>('/v1/drivers/');
    } catch (error) {
      console.error('Error fetching drivers data', error);
      driversError = error;
    }
    if (!isCurrent()) return;

    const assignUserOption = assignUserOptions(drivers);

    if (dialogType === DialogType.Add) {
      openDialog(
        t('add-device.title'),
        '',
        withDriversNote(addDeviceFields(t, {}, assignUserOption), driversError),
        DialogType.Add
      );
    } else if (
      (dialogType === DialogType.Edit || dialogType === DialogType.Delete) &&
      id
    ) {
      try {
        const deviceResponse = await axiosInstance.get(`/v1/devices/${id}/`);
        const deviceData = deviceResponse.data;
        setCurrentDeviceId(id);
        if (dialogType === DialogType.Edit) {
          if (deviceData.status === 'using') {
            deviceData.status = `using:${deviceData.user}`;
          }
          openDialog(
            t('edit-device.title'),
            '',
            withDriversNote(editDeviceFields(t, deviceData, assignUserOption), driversError),
            DialogType.Edit
          );
        } else if (dialogType === DialogType.Delete) {
          openDialog(
            t('delete-device.title'),
            t('delete-device.subtitle'),
            deleteDeviceFields(t, deviceData),
            DialogType.Delete
          );
        }
      } catch (error) {
        console.error('Failed to fetch device data', error);
        // ADM-15。開けなかった理由を出す（以前は押しても何も起きないように見えた）。
        showSaveError(error);
      }
    }
  };

  const handleAddDevice = async (
    e: FormEvent<Element>,
    newData: NewDataField
  ) => {
    e.preventDefault();

    let updatedStatus = newData.status;
    let driverId = null;

    if (
      typeof newData.status === 'string' &&
      newData.status.includes('using')
    ) {
      [updatedStatus, driverId] = newData.status.split(':');
    }

    const payload = { ...newData, status: updatedStatus, user: driverId };

    // EV-130（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
    // 同じ値・同じ本文で送り直す。結果が分からなかった後に入力を変えずに押したら前回と同じ値
    // （機器IDが空でも二重に作らず、機器IDがあっても「すでにある」ではなく前回の結果が返る）。
    // 24時間の期限・前回の値を残す条件・タブに残す・利用者の印は配送先の作成と共通
    // （config/idempotentCreate.ts）。送信中の入力・閉じるの停止は AddDialog。
    const outcome = await submitIdempotentCreate('device', '/v1/devices/', payload);
    if (outcome.status === 'created') {
      setIsAddDialogOpen(false);
      setDeviceData((prevData) => [...prevData, outcome.response.data]);
    } else if (outcome.status === 'expired') {
      // RV-127。期限が近く、前回の値は使えない。一覧を読み直して警告する（次の押下は新しい値）。
      await fetchDeviceData();
      setErrorMessages([t('add-device.create-retry-expired')]);
    } else if (outcome.status === 'unknown') {
      // 作成できたかを確かめられなかった（値は残した）。ダイアログと入力を残し、一覧を読み直す。
      console.error('Failed to add device:', outcome.error);
      await fetchDeviceData();
      setErrorMessages([t('add-device.create-outcome-unknown')]);
    } else {
      console.error('Failed to add device:', outcome.error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(outcome.error);
    }
  };

  const handleEditDevice = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    try {
      if (updatedData && updatedData.status !== null) {
        const statusValue = String(updatedData.status);

        const [updatedStatus, driverId] = statusValue.includes(':')
          ? statusValue.split(':')
          : [statusValue, null];

        updatedData.status = updatedStatus;
        updatedData.user = driverId;

        const response = await axiosInstance.patch(
          `/v1/devices/${updatedData.id}/`,
          updatedData
        );

        setDeviceData((prevData) =>
          prevData.map((device) =>
            device.id === updatedData.id ? response.data : device
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update device:', error);
      // ADM-15。ダイアログを開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };

  const handleDeleteDevice = async (id: string) => {
    try {
      await axiosInstance.delete(`/v1/devices/${id}/`);
      setDeviceData(deviceData.filter((device) => device.id !== id));
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete device:', error);
      showSaveError(error);
    }
  };

  return (
    <div>
      {/* EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
          右の組も、1024px 未満で収まらないときは中で折り返す。1024px 以上は1段のまま、右の組は縮めず（lg:shrink-0）、検索の欄
          （min-w-0）が縮む（#248 の独立照合の指摘）。 */}
      <div className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between">
        <div className="flex w-2/5 min-w-0 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="ml-auto flex flex-row max-lg:flex-wrap lg:shrink-0 justify-end gap-y-2 space-x-2 items-center">
          <SortBy
            options={sortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <AddButton
            label={t('add-device.title')}
            onClick={() => handleDialog(DialogType.Add)}
          />
          <AddDialog
            isOpen={isAddDialogOpen}
            onClose={closeDialog}
            handleSubmit={handleAddDevice}
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('add-device.save')}
            cancelLabel={t('add-device.cancel')}
            fields={dialogProps.fields}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditDevice(e, { ...updatedData, id: currentDeviceId ?? '' })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('edit-device.save')}
            cancelLabel={t('edit-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
          />
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, data: any) =>
              handleDeleteDevice(data.id || '')
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('delete-device.save')}
            cancelLabel={t('delete-device.cancel')}
            fields={dialogProps.fields}
            deviceId={currentDeviceId ?? ''}
          />
          <Export
            data={deviceData}
            name="devices"
            conditions={exportConditions}
            exportFields={[
              { header: 'Device ID', field: 'id' },
              { header: 'Device Name', field: 'name' },
              { header: 'Device Status', field: 'status' },
              { header: 'Assigned User Name', field: 'user_info.name' },
              { header: 'Assigned User Contact', field: 'user_info.email' },
              { header: 'Assigned Date', field: 'assigned_date' },
              { header: 'Purchase Date', field: 'purchase_date' },
              { header: 'Warranty Date', field: 'warranty_date' },
            ]}
          />
        </div>
      </div>

      {fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchDeviceData} />
      ) : (
        <DeviceTable
          devices={deviceData}
          onEdit={handleDialog.bind(null, DialogType.Edit)}
          onDelete={handleDialog.bind(null, DialogType.Delete)}
        />
      )}

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
      {errorMessages.length > 0 && (
        <ErrorPopup
          title={t('ui.error', { ns: 'common' })}
          messages={errorMessages}
          onClose={() => setErrorMessages([])}
        />
      )}
    </div>
  );
};

export default DeviceManagement;
